/**
 * TenantDetailsModal Component
 *
 * Displays detailed read-only information about a tenant.
 * Shows different sections based on isolation level.
 * Includes Backups tab for backup configuration and history.
 *
 * @module features/tenants/components/modals/TenantDetailsModal
 */

import { useState } from 'react';
import {
  DatabaseIcon,
  ExternalLinkIcon,
  EditIcon,
  ArchiveIcon,
  RefreshIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  BuildingIcon,
} from '../../../../components/common/Icons';
import { BackupsTabContent } from '../../../../components/backups';
import { NavigationVisibilityPanel } from './NavigationVisibilityPanel';
import AllocationsTab from '../../../../components/ai/AllocationsTab';
import { getStatusBadge, getIsolationBadge, getContainerStatusBadge } from '../../utils';
import type { Tenant, ContainerStatus } from '../../types';
import type { AvailableUpgrade } from '../../api/upgradeApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

type DetailTab = 'details' | 'backups' | 'navigation' | 'allocations';

interface TenantDetailsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The tenant to display */
  tenant: Tenant | null;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback to open edit mode */
  onEdit: (tenant: Tenant) => void;
  /** Container statuses by tenant ID */
  containerStatuses: Record<string, ContainerStatus>;
  /** Tenant ID currently archiving schema */
  isArchivingSchema: string | null;
  /** Callback to archive schema */
  onArchiveSchema: (tenant: Tenant) => void;
  /** Tenant ID currently provisioning schema */
  isProvisioningSchema: string | null;
  /** Callback to provision schema */
  onProvisionSchema: (tenantId: string) => void;
  /** Upgrade info for this tenant */
  upgradeInfo?: AvailableUpgrade | null;
  /** Callback to start upgrade */
  onUpgrade?: (tenant: Tenant) => void;
  /** Authenticated fetch function for API calls */
  authFetch?: AuthFetch;
  /** Whether the current user is a super admin */
  isSuperAdmin?: boolean;
  /** Callback to enter tenant context */
  onEnterTenant?: (tenant: Tenant) => void;
}

/**
 * Renders a detailed view modal for a tenant.
 */
export const TenantDetailsModal: React.FC<TenantDetailsModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onEdit,
  containerStatuses,
  isArchivingSchema,
  onArchiveSchema,
  isProvisioningSchema,
  onProvisionSchema,
  upgradeInfo,
  onUpgrade,
  authFetch,
  isSuperAdmin = false,
  onEnterTenant,
}) => {
  const showBackupsTab = tenant?.slug !== 'default';
  const [activeTab, setActiveTab] = useState<DetailTab>('details');

  if (!isOpen || !tenant) {
    return null;
  }

  const handleEdit = () => {
    onClose();
    onEdit(tenant);
  };

  const handleArchiveSchema = () => {
    onClose();
    onArchiveSchema(tenant);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: activeTab === 'navigation' ? '700px' : activeTab === 'backups' ? '820px' : activeTab === 'allocations' ? '900px' : '600px',
          maxHeight: '85vh',
          overflow: 'auto',
          transition: 'max-width 0.2s ease',
        }}
      >
        <div className="modal-header">
          <h2>{tenant.name}</h2>
          <button className="btn btn-ghost" onClick={onClose}>{'\u00d7'}</button>
        </div>

        {/* Tab Navigation */}
        <div style={{ padding: '0 24px' }}>
          <div className="tabs-list" style={{ marginBottom: '16px' }}>
            <button
              className={`tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            {showBackupsTab && (
              <button
                className={`tab ${activeTab === 'backups' ? 'active' : ''}`}
                onClick={() => setActiveTab('backups')}
              >
                <ArchiveIcon size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Backups
              </button>
            )}
            <button
              className={`tab ${activeTab === 'allocations' ? 'active' : ''}`}
              onClick={() => setActiveTab('allocations')}
            >
              Allocations
            </button>
            {isSuperAdmin && (tenant as Tenant & { template_id?: string }).template_id && (
              <button
                className={`tab ${activeTab === 'navigation' ? 'active' : ''}`}
                onClick={() => setActiveTab('navigation')}
              >
                Navigation
              </button>
            )}
          </div>
        </div>

        <div className="modal-body" style={{ paddingTop: 0 }}>
          {/* Backups Tab */}
          {activeTab === 'backups' && authFetch && (
            <BackupsTabContent
              tenantId={tenant.id}
              authFetch={authFetch}
              isSuperAdmin={isSuperAdmin}
              isSystemTenant={tenant.is_system === true}
            />
          )}
          {activeTab === 'backups' && !authFetch && (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
              Backup management is not available in this context.
            </div>
          )}

          {/* Navigation Tab */}
          {activeTab === 'navigation' && isSuperAdmin && authFetch && (
            <NavigationVisibilityPanel
              tenant={tenant}
              authFetch={authFetch}
              onSaved={() => {}}
            />
          )}

          {/* Allocations Tab — WQ-230 */}
          {activeTab === 'allocations' && (
            <AllocationsTab tenantId={tenant.id} />
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
          <div className="detail-grid" style={{ display: 'grid', gap: '16px' }}>
            {/* Basic Info */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Basic Info</h4>
              <div className="detail-row"><strong>ID:</strong> {tenant.id}</div>
              <div className="detail-row"><strong>URL Identifier:</strong> {tenant.slug}</div>
              <div className="detail-row"><strong>Status:</strong> {getStatusBadge(tenant.status)}</div>
              <div className="detail-row"><strong>Isolation:</strong> {getIsolationBadge(tenant.isolation_level)}</div>
              {(tenant as Tenant & { template_id?: string }).template_id && (
                <div className="detail-row">
                  <strong>Template:</strong>{' '}
                  <span className="badge badge-info" style={{ marginLeft: '4px' }}>
                    Template Assigned
                  </span>
                </div>
              )}
              {tenant.vertical && (
                <div className="detail-row"><strong>Vertical:</strong> {tenant.vertical}</div>
              )}
            </div>

            {/* Schema Information - for schema-level tenants */}
            {tenant.isolation_level === 'schema' && (
              <div className="detail-section" style={{
                background: 'rgba(124, 58, 237, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(124, 58, 237, 0.3)',
              }}>
                <h4 style={{ marginBottom: '12px', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DatabaseIcon size={18} />
                  Schema Information
                </h4>
                <div className="detail-row" style={{ marginBottom: '8px' }}>
                  <strong>Schema Status:</strong>{' '}
                  {tenant.schema_name ? (
                    <span className="badge badge-success">Provisioned</span>
                  ) : (
                    <span className="badge badge-warning">Not Provisioned</span>
                  )}
                </div>
                {tenant.schema_name && (
                  <div className="detail-row" style={{ marginBottom: '8px' }}>
                    <strong>Schema Name:</strong>{' '}
                    <code style={{
                      background: 'rgba(0,0,0,0.2)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                    }}>
                      {tenant.schema_name}
                    </code>
                  </div>
                )}
                <div className="detail-row" style={{ marginTop: '12px' }}>
                  {tenant.schema_name ? (
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={handleArchiveSchema}
                      disabled={isArchivingSchema === tenant.id}
                      style={{ fontSize: '12px' }}
                    >
                      {isArchivingSchema === tenant.id ? (
                        <><RefreshIcon size={14} className="spin" /> Archiving...</>
                      ) : (
                        <><ArchiveIcon size={14} /> Archive Schema</>
                      )}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => onProvisionSchema(tenant.id)}
                      disabled={isProvisioningSchema === tenant.id}
                      style={{ fontSize: '12px' }}
                    >
                      {isProvisioningSchema === tenant.id ? (
                        <><RefreshIcon size={14} className="spin" /> Provisioning...</>
                      ) : (
                        <><DatabaseIcon size={14} /> Provision Schema</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Container Access Information - for container isolation tenants */}
            {tenant.isolation_level === 'container' && (
              <div className="detail-section" style={{
                background: 'rgba(6, 182, 212, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(6, 182, 212, 0.3)',
              }}>
                <h4 style={{ marginBottom: '12px', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExternalLinkIcon size={18} />
                  Container Access
                </h4>
                <div className="detail-row" style={{ marginBottom: '8px' }}>
                  <strong>Status:</strong>{' '}
                  {containerStatuses[tenant.id] ? (
                    getContainerStatusBadge(containerStatuses[tenant.id].status)
                  ) : (
                    <span className="badge badge-secondary">Checking...</span>
                  )}
                </div>
                {containerStatuses[tenant.id]?.status === 'running' && (
                  <div className="detail-row" style={{ marginBottom: '8px' }}>
                    <strong>Access URL:</strong>{' '}
                    <code style={{
                      background: 'rgba(0,0,0,0.2)',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      userSelect: 'all',
                    }}>
                      {containerStatuses[tenant.id].access_url || 'Calculating...'}
                    </code>
                  </div>
                )}
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px' }}>
                  Tenant administrators can access their dedicated instance at this URL.
                  Login credentials were sent to: {tenant.admin_email}
                </p>
              </div>
            )}

            {/* Version Info - for container tenants */}
            {tenant.isolation_level === 'container' && (
              <div className="detail-section" style={{
                background: upgradeInfo?.has_upgrade
                  ? 'rgba(234, 179, 8, 0.1)'
                  : 'rgba(34, 197, 94, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                border: `1px solid ${upgradeInfo?.has_upgrade ? 'rgba(234, 179, 8, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
              }}>
                <h4 style={{ marginBottom: '12px', color: upgradeInfo?.has_upgrade ? 'var(--warning)' : 'var(--success)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {upgradeInfo?.has_upgrade ? <ArrowUpIcon size={18} /> : <CheckCircleIcon size={18} />}
                  Version
                </h4>
                <div className="detail-row" style={{ marginBottom: '8px' }}>
                  <strong>Current:</strong> {upgradeInfo ? `v${upgradeInfo.current_version}` : 'Checking...'}
                </div>
                <div className="detail-row" style={{ marginBottom: '8px' }}>
                  <strong>Latest:</strong> {upgradeInfo ? `v${upgradeInfo.latest_version}` : 'Checking...'}
                </div>
                {upgradeInfo?.has_upgrade && onUpgrade && (
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => { onClose(); onUpgrade(tenant); }}
                    style={{ fontSize: '12px', marginTop: '8px' }}
                  >
                    <ArrowUpIcon size={14} /> Upgrade to v{upgradeInfo.latest_version}
                  </button>
                )}
                {!upgradeInfo && (
                  <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Version data loading or unavailable.
                  </p>
                )}
              </div>
            )}

            {/* Contact */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Contact</h4>
              <div className="detail-row"><strong>Admin Email:</strong> {tenant.admin_email}</div>
              {tenant.admin_name && (
                <div className="detail-row"><strong>Admin Name:</strong> {tenant.admin_name}</div>
              )}
            </div>

            {/* Limits */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Limits</h4>
              <div className="detail-row"><strong>Max Users:</strong> {tenant.limits?.maxUsers || 100}</div>
              <div className="detail-row"><strong>Max Storage:</strong> {tenant.limits?.maxStorageGB || 50} GB</div>
              <div className="detail-row"><strong>API Calls/Day:</strong> {tenant.limits?.maxApiCallsPerDay || 10000}</div>
            </div>

            {/* AI Configuration */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>AI Configuration</h4>
              <div className="detail-row">
                <strong>AI Enabled:</strong> {tenant.ai_config?.enabled ? 'Yes' : 'No'}
              </div>
              {tenant.ai_config?.enabled && (
                <>
                  <div className="detail-row">
                    <strong>Daily Token Limit:</strong> {tenant.ai_config?.dailyTokenLimit?.toLocaleString() || 'Unlimited'}
                  </div>
                  <div className="detail-row">
                    <strong>Allowed Models:</strong> {tenant.ai_config?.modelsAllowed?.join(', ') || 'All'}
                  </div>
                </>
              )}
            </div>

            {/* Features */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Features</h4>
              <div className="detail-row">
                {tenant.features?.length > 0 ? (
                  <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                    {tenant.features.map(f => (
                      <span key={f} className="badge badge-info">{f}</span>
                    ))}
                  </div>
                ) : (
                  <span className="text-secondary">No feature flags enabled</span>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="detail-section">
              <h4 style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>Timestamps</h4>
              <div className="detail-row">
                <strong>Created:</strong> {new Date(tenant.created_at).toLocaleString()}
              </div>
              <div className="detail-row">
                <strong>Updated:</strong> {new Date(tenant.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {tenant.status === 'active' && onEnterTenant && (
            <button
              className="btn btn-primary"
              onClick={() => { onClose(); onEnterTenant(tenant); }}
              style={{ marginRight: 'auto' }}
            >
              <BuildingIcon size={14} /> Enter Tenant Context
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleEdit}
          >
            <EditIcon size={14} /> Edit
          </button>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
